import { useItemSet } from '@exam-management/common/item-set-provider';
import { useConfig } from '@exam-management/configuration';
import { QuestionGroupStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useNavigate, useParams } from '@exam-management/router';
import { AddRoundedIcon, Button, Tooltip } from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import type { PropsWithChildren } from '@shared/utils';

import { BaseItemSetSubQuestionSection } from '../../components';

type ItemSetSubQuestionSectionProps = PropsWithChildren;

export default function ItemSetSubQuestionSection(props: Readonly<ItemSetSubQuestionSectionProps>) {
  const { children } = props;
  const configuration = useConfig();

  const { structureId, questionId } = useParams(
    '/qbank-structures/:structureId/qcontents/item-set/:questionId',
  );

  const navigate = useNavigate();

  const { itemSet } = useItemSet();
  const { id, status } = itemSet;

  const hasCreatePermission = useHasPermission(APP_PERMISSIONS.QBANK.QUESTION.CREATE);

  const shouldShowAddButton = status === QuestionGroupStatus.Draft && hasCreatePermission;

  const maxSubQuestions = configuration.modules.qcontent.maxSubQuestions;
  const isReachMaxSubQuestions = (itemSet.questions?.length ?? 0) >= maxSubQuestions;

  const shouldDisableAddButton = !id || isReachMaxSubQuestions; // NOTE: if id is empty, means that the item set is not created yet.

  const handleClick = () => {
    navigate('/qbank-structures/:structureId/qcontents/item-set/:questionId/create', {
      params: {
        structureId,
        questionId,
      },
    });
  };

  return (
    <BaseItemSetSubQuestionSection
      slotProps={{
        title: {
          endAdornment: shouldShowAddButton ? (
            <Tooltip
              placement='top'
              arrow
              title={
                isReachMaxSubQuestions ? 'ไม่สามารถเพิ่ม Sub Question ได้อีก (สูงสุด 5 ข้อ)' : ''
              }
            >
              <Button
                variant='outlined'
                startIcon={<AddRoundedIcon />}
                disabled={shouldDisableAddButton}
                onClick={handleClick}
                sx={(theme) => ({
                  '&.Mui-disabled': {
                    pointerEvents: 'auto',
                    bgcolor: theme.palette.grey[100],
                    ':hover': {
                      borderColor: theme.palette.grey['300'],
                    },
                  },
                })}
              >
                Add Sub Question
              </Button>
            </Tooltip>
          ) : null,
        },
      }}
    >
      {children}
    </BaseItemSetSubQuestionSection>
  );
}
